import {
  array,
  boolean,
  date,
  number,
  object,
  string,
  ZodIssueCode,
  discriminatedUnion,
  literal,
  preprocess,
  union,
  ZodObject,
  ZodRawShape,
} from 'zod';

import { PromocodesOfferType } from '@occc/graphql';
import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { IS_REQUIRED, isStringLengthUpTo } from '@occc-app/lib/constants/validationMessages';
import { AwardType } from '@occc-app/offerHelpers/types';
import {
  CustomDataSchema,
  OfferNameAndDescriptionSchema,
  AwardRestrictionsSchema,
  AwardPointsSchema,
  AwardVouchersConstraintSchema,
  FulfillmentWindowSchema,
  withIsEnabled,
} from '@occc/validation';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';

import {
  MAX_PROMOCODE_DESCRIPTION_LENGTH,
  MAX_PROMOCODE_VALUE_LENGTH,
  MAX_PROMOCODES_LENGTH,
  MIN_PROMOCODE_VALUE_LENGTH,
  PROMOCODE_VALIDATION_MESSAGE,
} from './promocodes-configuration.constants';
export const PromocodeSchema = object({
  id: string(),
  isSaved: boolean().optional(),
  redemptions: number().optional(),
  code: string({ required_error: IS_REQUIRED })
    .min(MIN_PROMOCODE_VALUE_LENGTH, IS_REQUIRED)
    .max(MAX_PROMOCODE_VALUE_LENGTH, PROMOCODE_VALIDATION_MESSAGE)
    .regex(/^[A-Z0-9]*$/, PROMOCODE_VALIDATION_MESSAGE),
  description: string()
    .max(MAX_PROMOCODE_DESCRIPTION_LENGTH, isStringLengthUpTo(MAX_PROMOCODE_DESCRIPTION_LENGTH))
    .nullable()
    .optional(),
  startDate: date({ required_error: IS_REQUIRED }),
  endDate: date().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.startDate && data.endDate && data.endDate < data.startDate) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['endDate'],
      message: 'End date must be later than start date',
    });
  }
});

export const PromocodesGlobalBaseSchema = object({
  codesType: literal(PromocodesOfferType.Global),
  promocodes: array(PromocodeSchema).max(MAX_PROMOCODES_LENGTH),
});

export const PromocodesIndividualBaseSchema = object({
  codesType: literal(PromocodesOfferType.Individual),
});

const createPromocodesAwardTypeSchema = <T extends ZodObject<ZodRawShape>>(baseSchema: T) =>
  discriminatedUnion('awardType', [
    baseSchema.extend({
      awardType: literal(AwardType.AwardPoints),
      awardPoints: preprocess((data) => data ?? {}, AwardPointsSchema),
    }),
    baseSchema.extend({
      awardType: literal(AwardType.AwardVouchers),
      awardVouchers: preprocess((data) => data ?? {}, AwardVouchersConstraintSchema),
    }),
    baseSchema.extend({
      awardType: literal(AwardType.AwardNullable),
    }),
  ]);

export const PromocodesSchema = union([
  createPromocodesAwardTypeSchema(PromocodesGlobalBaseSchema),
  createPromocodesAwardTypeSchema(PromocodesIndividualBaseSchema),
]);

export const ConfigurePromocodesAwardSchema = createPromocodesAwardTypeSchema(object({}));

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({
    membership: data.membership,
    global: data.global,
    ctx,
  });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
});

export const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

export const PromoCodeGroupFormSchema = object({
  id: string(),
  fileName: string(),
  processingStatus: string(),
  codesStats: object({
    createdCount: number(),
    failedCount: number(),
    totalCount: number(),
  }),
});

export const promocodesFormSchema = object({
  promocodes: PromocodesSchema.optional(),
});

export const validatePromocodes = makeValidate(promocodesFormSchema);

export const configurationSchema = OfferNameAndDescriptionSchema.extend({
  earningMechanism: object({
    id: string().optional(),
  }),
  promocodes: ConfigurePromocodesAwardSchema,
  fulfillmentWindow: FulfillmentWindowSchema,
  awardCount: AwardCountSchema,
  awardRestrictions: AwardRestrictionsSchema,
  customData: CustomDataSchema,
});

export const validateConfiguration = makeValidate(configurationSchema);
